import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, Fish, Anchor, Sun, MapPin } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import { Reveal } from "@/components/site/Reveal";
import { PHOTO_SIZES, PHOTOS } from "@/components/site/images";
import {
  ACTIVITIES_PATH,
  ADDRESS_ONE_LINE,
  BOOKING_URL,
  BUSINESS,
  CAMPGROUND_ID,
  CONTACT_PATH,
  FISHING_GUIDE_PATH,
  MAPS_URL,
  ROOMS_PATH,
  routeTo,
} from "@/lib/site";

const TITLE = "Falcon Lake Bass Fishing Guide | Fishing Near Zapata, TX";
const DESCRIPTION =
  "A local guide to Falcon Lake bass fishing near Zapata, TX: boat ramps, season-by-season tips, common catches, licenses and where to stay at Amigo Inn & RV Park.";
const CANONICAL = `https://amigoinn-rvpark.com${FISHING_GUIDE_PATH}`;
const OG_IMAGE = `https://amigoinn-rvpark.com${PHOTOS.coveredParking.src}`;

const RAMPS = [
  {
    name: "Zapata County Boat Ramp (Zapata)",
    detail:
      "The closest public launch to the park — minutes from your site, with paved parking and trailer turnaround. The usual choice for pre-dawn launches on the lower end of the lake.",
  },
  {
    name: "Falcon State Park Ramp (Falcon Heights)",
    detail:
      "About 40 minutes south. Multiple lanes, restrooms and protected water, which makes it the safer bet when a south wind is pushing hard across the main lake.",
  },
  {
    name: "San Ygnacio / upper-lake access",
    detail:
      "North of Zapata toward the river channel. Handy when water levels drop and anglers work the timber and creek arms up the Rio Grande end.",
  },
  {
    name: "Check levels before you tow",
    detail:
      "Falcon fluctuates a lot. Confirm the current elevation and which ramps are usable with IBWC lake data or Texas Parks & Wildlife before hitting the road.",
  },
];

const SEASONS = [
  {
    icon: Sun,
    season: "Spring (Feb–May)",
    body: "Prime time. Prespawn and spawning largemouth move onto flats and hardwood in 2–8 feet. Big swimbaits, lipless cranks, Texas-rigged creature baits and wacky-rigged senkos shine as water hits the mid-60s.",
  },
  {
    icon: Fish,
    season: "Summer (Jun–Sep)",
    body: "Fish deep and early. Work 12–25 feet on humps, roadbeds and standing timber with deep crankbaits, drop shots and big worms. Topwater at first light, then chase shad schools with a jigging spoon.",
  },
  {
    icon: Anchor,
    season: "Fall (Oct–Nov)",
    body: "Bait balls move into creek arms and the bass follow. Spinnerbaits, squarebills and walking topwaters over shallow brush produce fast, aggressive bites in comfortable weather.",
  },
  {
    icon: MapPin,
    season: "Winter (Dec–Jan)",
    body: "Mild South Texas winters keep Falcon fishable year-round. Slow down with jigs, Carolina rigs and suspending jerkbaits on rock and drop-offs — this is when many trophy fish get weighed in.",
  },
];

const CATCHES = [
  {
    name: "Largemouth bass",
    body: "Falcon's headline fish and the reason tournaments keep coming back. Double-digit largemouth are a genuine possibility, with 3–6 pound fish making up most good days.",
  },
  {
    name: "White bass",
    body: "Schooling and plentiful. Small jigs, slabs and spoons over open water will keep rods bent when the largemouth bite slows.",
  },
  {
    name: "Channel & blue catfish",
    body: "Strong numbers on flats and in the river channel. Cut bait or prepared bait on the bottom works day or night.",
  },
  {
    name: "Crappie & sunfish",
    body: "Found around standing timber, brush and bridge structure — a good option for kids and light-tackle mornings.",
  },
];

const FAQ = [
  {
    q: "Is Falcon Lake good for bass fishing?",
    a: "Falcon Lake is one of the best-known largemouth bass fisheries in Texas. Its shallow hardwood, rock structure and long growing season regularly produce heavy tournament limits and trophy-class fish.",
  },
  {
    q: "When is the best time to fish Falcon Lake?",
    a: "Late February through May is the peak window for numbers and size, when prespawn and spawning bass move shallow. The lake fishes well all year thanks to mild South Texas winters.",
  },
  {
    q: "Do I need a Texas fishing license on Falcon Lake?",
    a: "Yes. A valid Texas fishing license with a freshwater endorsement is required for anglers 17 and older. Falcon is an international reservoir, so stay on the U.S. side of the boundary unless you hold the appropriate Mexican permits.",
  },
  {
    q: "Where should I stay for fishing near Zapata, TX?",
    a: "Amigo Inn & RV Park is at 209 Lake Shore Dr in Zapata, minutes from public boat access. We offer motel rooms, park models and full-hookup RV sites plus covered boat and trailer parking.",
  },
];

export const Route = createFileRoute("/activities_/falcon-lake-fishing-guide")({
  component: FishingGuidePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Falcon Lake Bass Fishing Guide: Ramps, Seasons and Catches Near Zapata, TX",
          description: DESCRIPTION,
          url: CANONICAL,
          image: OG_IMAGE,
          author: { "@type": "Organization", name: BUSINESS.name, url: BUSINESS.siteUrl },
          publisher: { "@type": "Organization", name: BUSINESS.name, url: BUSINESS.siteUrl },
          about: { "@id": CAMPGROUND_ID },
          isPartOf: { "@type": "WebSite", name: BUSINESS.name, url: BUSINESS.siteUrl },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: BUSINESS.siteUrl },
            {
              "@type": "ListItem",
              position: 2,
              name: "Activities",
              item: `https://amigoinn-rvpark.com${ACTIVITIES_PATH}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Falcon Lake Bass Fishing Guide",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
});

function FishingGuidePage() {
  const hero = PHOTOS.coveredParking;

  return (
    <div className="min-h-dvh bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-forest"
      >
        Skip to content
      </a>

      <SiteHeader />

      <main id="main">
        {/* Hero */}
        <section className="relative isolate overflow-hidden bg-forest-deep py-24 sm:py-28 lg:py-32">
          <img
            src={hero.src}
            srcSet={hero.srcSet}
            sizes={PHOTO_SIZES.fullBleed}
            alt=""
            width={hero.width}
            height={hero.height}
            className="absolute inset-0 -z-10 size-full object-cover"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,oklch(0.259_0.042_163.6/0.94)_0%,oklch(0.259_0.042_163.6/0.8)_50%,oklch(0.259_0.042_163.6/0.6)_100%)]"
            aria-hidden="true"
          />
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-3xl text-cream">
              <p className="eyebrow text-sand">Fishing Near Zapata, Texas</p>
              <h1 className="mt-5 text-balance font-display text-3xl leading-[1.08] sm:text-4xl lg:text-[3.25rem]">
                Falcon Lake Bass Fishing Guide
              </h1>
              <p className="mt-6 text-base leading-relaxed text-cream/85 sm:text-lg">
                Falcon Lake sits on the Rio Grande just south of Zapata and has earned a national
                reputation for heavy largemouth bass. This guide covers the boat ramps closest to
                the park, what works season by season, the species you can expect, and the practical
                details worth knowing before you tow.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-sm font-semibold text-cream"
                >
                  Check Availability <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline text-sm font-semibold text-sand"
                >
                  Get Directions <ArrowUpRight className="size-4" aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Boat ramps */}
        <section className="bg-background py-20 sm:py-24" aria-labelledby="ramps-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Getting On The Water</p>
              <h2
                id="ramps-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                Falcon Lake Boat Ramps &amp; Public Access
              </h2>
              <p className="mt-5 text-base leading-relaxed text-forest-deep/80 sm:text-lg">
                Amigo Inn &amp; RV Park is at {ADDRESS_ONE_LINE}, a short drive from Zapata&apos;s
                public launch. Rig your boat the night before and be idling out before sunrise.
              </p>
            </Reveal>

            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {RAMPS.map((ramp, i) => (
                <Reveal
                  key={ramp.name}
                  delay={i * 0.06}
                  className="rounded-sm border border-forest/15 bg-surface p-7"
                >
                  <h3 className="font-display text-xl text-forest-deep">{ramp.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{ramp.detail}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Seasonal tips */}
        <section className="bg-surface py-20 sm:py-24" aria-labelledby="seasons-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Season By Season</p>
              <h2
                id="seasons-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                When To Fish Falcon Lake — And What To Throw
              </h2>
            </Reveal>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {SEASONS.map((s, i) => (
                <Reveal
                  key={s.season}
                  delay={i * 0.06}
                  className="rounded-sm border border-forest/15 bg-background p-7"
                >
                  <s.icon className="size-6 text-clay" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-xl text-forest-deep">{s.season}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{s.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Common catches */}
        <section className="bg-background py-20 sm:py-24" aria-labelledby="catches-title">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-clay">Common Catches</p>
              <h2
                id="catches-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                What You&apos;ll Catch On Falcon
              </h2>
            </Reveal>

            <ul className="mt-12 grid list-none gap-x-10 gap-y-8 p-0 sm:grid-cols-2">
              {CATCHES.map((c, i) => (
                <Reveal
                  key={c.name}
                  as="li"
                  delay={i * 0.05}
                  className="border-t border-forest/15 pt-6"
                >
                  <p className="font-display text-xl text-forest-deep">{c.name}</p>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{c.body}</p>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Practical notes + FAQ */}
        <section className="bg-surface py-20 sm:py-24" aria-labelledby="faq-title">
          <div className="mx-auto grid max-w-[1400px] gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
            <Reveal>
              <p className="eyebrow text-clay">Before You Go</p>
              <h2
                id="faq-title"
                className="mt-5 text-balance font-display text-3xl leading-[1.12] text-forest-deep sm:text-4xl"
              >
                Licenses, Limits &amp; Local Notes
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-forest-deep/80">
                <p>
                  Anglers 17 and older need a valid Texas fishing license with a freshwater
                  endorsement. Falcon is an international reservoir shared with Mexico — know where
                  the boundary sits and carry the right permits if you plan to cross it.
                </p>
                <p>
                  Water levels swing widely from year to year, which changes both the productive
                  structure and which ramps are usable. Check current elevation before you tow, and
                  watch afternoon south winds on the main lake.
                </p>
                <p>
                  Stay at Amigo Inn and you can park the boat under cover, rinse gear on site, and
                  be at the ramp in minutes.{" "}
                  <Link to={routeTo(CONTACT_PATH)} className="link-underline font-semibold">
                    Call or message us
                  </Link>{" "}
                  and we&apos;ll help you plan the trip.
                </p>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                <Link
                  to={routeTo(ROOMS_PATH)}
                  className="link-underline text-sm font-semibold text-forest"
                >
                  Rooms &amp; RV Sites <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
                <Link
                  to={routeTo(ACTIVITIES_PATH)}
                  className="link-underline text-sm font-semibold text-clay"
                >
                  All Activities <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>

            <Reveal x={16} className="space-y-8">
              {FAQ.map((item) => (
                <div key={item.q} className="border-t border-forest/15 pt-6">
                  <h3 className="font-display text-xl text-forest-deep">{item.q}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-forest-deep/75">{item.a}</p>
                </div>
              ))}
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter
        newsletterHeading="Fishing Reports & Park Updates"
        newsletterDescription="Get Falcon Lake fishing notes, seasonal rates and news from Amigo Inn & RV Park."
      />
      <MobileBookingBar />
    </div>
  );
}
